import React, { useState, useEffect } from 'react';
import Classes from './Dashboard.module.css';
import { AnimatePresence } from 'framer-motion';
import Hero1 from './Comp/Hero1';
import Hero3 from './Comp/Hero3';
import TrialStrip from './Comp/TrialStrip';
import TrialEndedPopup from '../../../../GlobalComponents/TrialEndedPopup/TrialEndedPopup';
import backend from '../../../../../api';
import RecordTable from './Comp/RecordTable/RecordTable';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AnalyzingPopup from '../../../../GlobalComponents/AnalyzingPopup/AnalyzingPopup';
import DataFoundPopup from '../../../../GlobalComponents/DataFoundPopup/DataFoundPopup';

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [trialEnded, setTrialEnded] = useState(false);
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [remainingDays, setRemainingDays] = useState(0);
  const [allExposures, setAllExposures] = useState([]);
  const [reports, setReports] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDataFoundPopup, setShowDataFoundPopup] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const joiningDate = user?.createdAt;
  const subscription = user?.subscription;
  const userId = user?._id;
  const navigateTo = useNavigate();

  const handleCloseTrialEnded = () => {
    setTrialEnded(false);
  };

  const handleCloseDataFoundPopup = () => {
    setShowDataFoundPopup(false);
  };

  // Load cached data from localStorage on mount
  useEffect(() => {
    try {
      const cachedData = localStorage.getItem(`privacyBotDashboard_${userId}`);
      if (cachedData) {
        const { reports, exposures } = JSON.parse(cachedData);
        setReports(reports);
        setAllExposures(exposures);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
    }
  }, [userId]);

  // Fetch new data and compare with localStorage
  useEffect(() => {
    const fetchAllExposures = async () => {
      try {
        setLoading(true);
        const response = await backend.get(`/user-information?userId=${userId}`);
        const fetchedReports = response?.data || [];

        let cachedReports = [];
        try {
          const cachedData = localStorage.getItem(`privacyBotDashboard_${userId}`);
          if (cachedData) {
            cachedReports = JSON.parse(cachedData).reports || [];
          }
        } catch (error) {
          console.error('Error reading cached data:', error);
        }

        const hasUpdatedExposures = (newReports, cachedReports) => {
          const cachedExposureMap = new Map();
          for (const report of cachedReports) {
            for (const exposure of report.exposures || []) {
              cachedExposureMap.set(exposure._id, exposure.infoExposed);
            }
          }

          for (const newReport of newReports) {
            for (const newExposure of newReport.exposures || []) {
              const cachedInfoExposed = cachedExposureMap.get(newExposure._id);
              if (!cachedInfoExposed) {
                if (Object.values(newExposure.infoExposed).some(val => val === true)) {
                  return true;
                }
              } else {
                for (const key in newExposure.infoExposed) {
                  if (
                    newExposure.infoExposed[key] === true &&
                    cachedInfoExposed[key] !== true
                  ) {
                    return true;
                  }
                }
              }
            }
          }
          return false;
        };

        const hasUpdates = hasUpdatedExposures(fetchedReports, cachedReports);

        const exposures = fetchedReports.flatMap(report =>
          report.exposures.map(exposure => ({
            ...exposure,
            reportId: report._id,
            reportTitle: `Exposure Report: ${new Date(report.createdAt).toLocaleString('default', { month: 'long', year: 'numeric' })}`
          }))
        );

        try {
          localStorage.setItem(
            `privacyBotDashboard_${userId}`,
            JSON.stringify({ reports: fetchedReports, exposures })
          );
        } catch (error) {
          console.error('Error writing to localStorage:', error);
        }

        setReports(fetchedReports);
        setAllExposures(exposures);
        setShowDataFoundPopup(hasUpdates);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching all exposures:', error);
        setLoading(false);
      }
    };

    fetchAllExposures();
  }, [userId, refresh]);

  const filteredExposures = allExposures.filter(exposure => {
    const query = searchQuery.toLowerCase();
    return (
      exposure.brokerName?.toLowerCase().includes(query) ||
      exposure.brokerEmail?.toLowerCase().includes(query) ||
      exposure.reportTitle?.toLowerCase().includes(query)
    );
  });

  const handleAddOrder = () => {
    navigateTo('/information');
  };

  const [analyzingPopup, setAnalyzingPopup] = useState(false);
  const handleAnalyzingPopup = () => {
    setAnalyzingPopup(true);
  };
  const handleSubmitAnalyzingPopup = () => {
    setAnalyzingPopup(false);
    localStorage.removeItem('analyzing');
  };

  useEffect(() => {
    const isAnalyzing = localStorage.getItem('analyzing');
    if (isAnalyzing) {
      setTimeout(() => {
        setAnalyzingPopup(true);
      }, 2000);
    }
  }, []);

  // Trial period logic
  useEffect(() => {
    if (!user) return;

    const joiningDate = new Date(user.createdAt);
    const currentDate = new Date();
    const trialEndDate = new Date(joiningDate.getTime() + 14 * 24 * 60 * 60 * 1000);

    if (currentDate < trialEndDate) {
      const remainingMs = trialEndDate - currentDate;
      const daysLeft = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
      setRemainingDays(daysLeft);
      setIsTrialActive(true);
      setTrialEnded(false);
    } else {
      setIsTrialActive(false);
      setRemainingDays(0);
      if (user.subscription === "free") {
        setTrialEnded(true);
      } else {
        setTrialEnded(false);
      }
    }
  }, [user]);

  return (
    <>

      <AnimatePresence>
        {trialEnded && <TrialEndedPopup closePopup={handleCloseTrialEnded} />}
      </AnimatePresence>
      <AnimatePresence>
        {analyzingPopup && <AnalyzingPopup handleSubmit={handleSubmitAnalyzingPopup} />}
      </AnimatePresence>
      <AnimatePresence>
        {showDataFoundPopup && <DataFoundPopup handleSubmit={handleCloseDataFoundPopup} />}
      </AnimatePresence>


      <div className={Classes.hero}>
        {isTrialActive && <TrialStrip remainingDays={remainingDays} />}

        <Hero1 exposures={allExposures} reports={reports} />
        {loading ? (
          <h5 className='lightText' style={{ margin: '20px 0 0 10px' }}>Loading...</h5>
        ) : (
          <RecordTable exposures={filteredExposures} setRefresh={setRefresh} />
        )}
        <Hero3 />
      </div>
    </>
  );
}